(() => {
  if (window.FleetPacksHeroSlideshow) {
    window.FleetPacksHeroSlideshow.init(document);
    return;
  }

  const instances = new WeakMap();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function init(root) {
    const heroes = root.matches?.('.fp-hero') ? [root] : root.querySelectorAll('.fp-hero');

    heroes.forEach((hero) => {
      if (instances.has(hero)) return;

      // Someone who has asked for less motion gets the video's still frame.
      const videos = [...hero.querySelectorAll('.fp-hero__video')];
      const holdVideos = () => {
        if (reducedMotion.matches) videos.forEach((video) => video.pause());
      };
      holdVideos();

      const slides = [...hero.querySelectorAll('[data-fp-hero-slide]')];
      const controls = hero.querySelector('[data-fp-hero-controls]');
      if (slides.length < 2 || !controls) {
        reducedMotion.addEventListener?.('change', () => {
          if (reducedMotion.matches) holdVideos();
          else videos.forEach((video) => video.play().catch(() => {}));
        });
        return;
      }

      const dots = [...controls.querySelectorAll('[data-fp-hero-to]')];
      const pauseButton = controls.querySelector('[data-fp-hero-pause]');
      const visibleHeading = hero.querySelector('[data-fp-hero-visible-heading]');
      const visibleText = hero.querySelector('[data-fp-hero-visible-text]');
      const interval = Math.max(4000, Number(controls.dataset.interval) || 6000);
      let index = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
      let timer;
      let pausedByUser = false;
      let pausedByInteraction = false;

      const stop = () => {
        window.clearTimeout(timer);
        timer = undefined;
      };

      /* A video slide is timed by the video itself: the slideshow moves on when
         it ends, not after the usual interval, so it is never cut off part way.
         A video that cannot play falls back to the interval like an image. */
      const videoOf = (slide) => slide?.querySelector('video') || null;
      const playable = (video) => video && !video.error;
      const holdingStill = () => pausedByUser || reducedMotion.matches;
      const syncVideos = (restart) => {
        slides.forEach((slide, slideIndex) => {
          const video = videoOf(slide);
          if (!video) return;
          if (slideIndex !== index || holdingStill()) {
            video.pause();
            return;
          }
          if (restart) video.currentTime = 0;
          video.play().catch(() => {});
        });
      };

      const show = (next) => {
        index = (next + slides.length) % slides.length;
        const activeSlide = slides[index];
        slides.forEach((slide, slideIndex) => {
          slide.classList.toggle('is-active', slideIndex === index);
        });
        if (visibleHeading) {
          visibleHeading.textContent = activeSlide.dataset.fpHeroHeading || hero.dataset.fpHeroDefaultHeading || '';
        }
        if (visibleText) {
          visibleText.textContent = activeSlide.dataset.fpHeroText || hero.dataset.fpHeroDefaultText || '';
        }
        dots.forEach((dot, dotIndex) => {
          dot.setAttribute('aria-pressed', String(dotIndex === index));
        });
        syncVideos(true);
      };

      const start = () => {
        stop();
        if (controls.dataset.autoplay !== 'true' || pausedByUser || pausedByInteraction || reducedMotion.matches || document.hidden) return;
        if (playable(videoOf(slides[index]))) return; // its end moves the slideshow on
        timer = window.setTimeout(() => {
          show(index + 1);
          start();
        }, interval);
      };

      /* When the video ends: move on, unless the slideshow is paused or set not
         to change by itself, in which case it plays again from the start. */
      videos.forEach((video) => {
        video.addEventListener('ended', () => {
          if (videoOf(slides[index]) !== video) return;
          const advancing = controls.dataset.autoplay === 'true'
            && !pausedByUser && !pausedByInteraction && !document.hidden;
          if (advancing) {
            show(index + 1);
            start();
          } else if (!holdingStill()) {
            video.currentTime = 0;
            video.play().catch(() => {});
          }
        });
        video.addEventListener('error', () => {
          if (videoOf(slides[index]) === video) start();
        });
      });

      controls.querySelector('[data-fp-hero-prev]')?.addEventListener('click', () => {
        show(index - 1);
        start();
      });
      controls.querySelector('[data-fp-hero-next]')?.addEventListener('click', () => {
        show(index + 1);
        start();
      });
      dots.forEach((dot) => dot.addEventListener('click', () => {
        show(Number(dot.dataset.fpHeroTo));
        start();
      }));
      pauseButton?.addEventListener('click', () => {
        pausedByUser = !pausedByUser;
        pauseButton.setAttribute('aria-pressed', String(pausedByUser));
        pauseButton.setAttribute('aria-label', pausedByUser ? 'Play slideshow' : 'Pause slideshow');
        pauseButton.textContent = pausedByUser ? '▶' : 'Ⅱ';
        syncVideos(false);
        start();
      });

      hero.addEventListener('mouseenter', () => { pausedByInteraction = true; stop(); });
      hero.addEventListener('mouseleave', () => { pausedByInteraction = false; start(); });
      hero.addEventListener('focusin', () => { pausedByInteraction = true; stop(); });
      hero.addEventListener('focusout', (event) => {
        if (hero.contains(event.relatedTarget)) return;
        pausedByInteraction = false;
        start();
      });
      const onMotionChange = () => {
        syncVideos(false);
        start();
      };
      document.addEventListener('visibilitychange', start);
      reducedMotion.addEventListener?.('change', onMotionChange);

      const onBlockSelect = (event) => {
        if (!hero.contains(event.target)) return;
        const selected = slides.findIndex((slide) => slide.dataset.blockId === event.detail?.blockId);
        if (selected < 0) return;
        show(selected);
        pausedByInteraction = true;
        stop();
      };
      const onBlockDeselect = () => { pausedByInteraction = false; start(); };
      document.addEventListener('shopify:block:select', onBlockSelect);
      document.addEventListener('shopify:block:deselect', onBlockDeselect);

      instances.set(hero, () => {
        stop();
        document.removeEventListener('visibilitychange', start);
        reducedMotion.removeEventListener?.('change', onMotionChange);
        document.removeEventListener('shopify:block:select', onBlockSelect);
        document.removeEventListener('shopify:block:deselect', onBlockDeselect);
      });
      show(index);
      start();
    });
  }

  window.FleetPacksHeroSlideshow = { init };
  init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('.fp-hero').forEach((hero) => {
      instances.get(hero)?.();
      instances.delete(hero);
    });
  });
})();
